import React from 'react';
import { Users, UserPlus, Shield, Send, Loader2, Trash2, Clock, Mail, X, Settings } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import TeamCapacityPanel from './TeamCapacityPanel';

export default function TeamManagementSection({ user, teamMembers, teamInvites, globalDetailedUsage, globalUsage,
  inviteEmail, setInviteEmail, inviteTeamMember, teamLoading, removeTeamMember, resendInvite, cancelInvite, onChanged }) {
  return (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>
                    <p className="text-gray-600 mt-1">
                      Manage your team members and collaboration settings
                    </p>
                  </div>
                </div>

                <TeamCapacityPanel user={user} onChanged={onChanged} />
                {/* Team Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="p-6">
                    <div className="flex items-center space-x-3">
                      <Users className="h-8 w-8 text-blue-600" />
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {teamMembers.filter(member => member.access_status !== 'suspended_plan_limit').length}
                        </div>
                        <div className="text-sm text-gray-600">Active Members</div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center space-x-3">
                      <UserPlus className="h-8 w-8 text-green-600" />
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {teamInvites.length}
                        </div>
                        <div className="text-sm text-gray-600">Pending Invites</div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-8 w-8 text-purple-600" />
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {(() => {
                            // Get team member limit from subscription data
                            const limit = globalDetailedUsage?.team_members_limit || globalUsage?.team_members_limit;
                            if (limit === -1) return '∞';
                            if (limit === undefined || limit === null) {
                              // Fallback to plan-based limits if subscription data not available
                              const planCode = globalDetailedUsage?.plan_code || globalUsage?.current_plan;
                              if (planCode === 'STARTER' || planCode === 'starter') return '10';
                              if (planCode === 'GROWTH' || planCode === 'pro') return '15';
                              if (planCode === 'BUSINESS' || planCode === 'business') return '∞';
                              if (planCode === 'ENTERPRISE' || planCode === 'enterprise') return '∞';
                              return '1'; // Default for TRIAL
                            }
                            return limit;
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">Member Limit</div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Invite New Member */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
                    Invite New Team Member
                  </h3>
                  <div className="flex space-x-4">
                    <Input
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && inviteTeamMember()}
                    />
                    <Button
                      onClick={inviteTeamMember}
                      disabled={teamLoading || !inviteEmail.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {teamLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Inviting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Invite
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Team members will have access to all tests and can collaborate on your projects.
                  </p>
                </Card>

                {/* Current Team Members */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-green-600" />
                    Team Members ({teamMembers.filter(member => member.access_status !== 'suspended_plan_limit').length})
                  </h3>

                  {teamMembers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No team members yet</p>
                      <p className="text-sm">Invite colleagues to start collaborating</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {teamMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium">
                                {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{member.name || 'Team Member'}</div>
                              <div className="text-sm text-gray-600">{member.email}</div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Badge className={`${member.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                              {member.access_status === 'suspended_plan_limit' ? 'Suspended by member limit' : member.role || 'Member'}
                            </Badge>
                            {member.id !== user.id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeTeamMember(member.id, member.name || member.email)}
                                disabled={teamLoading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Pending Invitations */}
                {teamInvites.length > 0 && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-orange-600" />
                      Pending Invitations ({teamInvites.length})
                    </h3>

                    <div className="space-y-3">
                      {teamInvites.map((invite) => (
                        <div key={invite.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                              <Mail className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{invite.email}</div>
                              <div className="text-sm text-gray-600">
                                Invited {new Date(invite.created_at || invite.invited_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Badge className="bg-orange-100 text-orange-800">
                              Pending
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendInvite(invite.id, invite.email)}
                              disabled={teamLoading}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Resend Invitation"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => cancelInvite(invite.id, invite.email)}
                              disabled={teamLoading}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Cancel Invitation"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Team Settings */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-gray-600" />
                    Team Settings
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Subscription Plan</div>
                        <div className="text-sm text-gray-600">
                          {globalDetailedUsage?.plan_name || globalUsage?.plan_name || 'Current Plan'}
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        Team Enabled
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Team Member Limit</div>
                        <div className="text-sm text-gray-600">
                          {(() => {
                            // Get team member limit from subscription data
                            const limit = globalDetailedUsage?.team_members_limit || globalUsage?.team_members_limit;
                            let displayLimit;

                            if (limit === -1) {
                              displayLimit = '∞';
                            } else if (limit === undefined || limit === null) {
                              // Fallback to plan-based limits if subscription data not available
                              const planCode = globalDetailedUsage?.plan_code || globalUsage?.current_plan;
                              if (planCode === 'STARTER' || planCode === 'starter') displayLimit = '10';
                              else if (planCode === 'GROWTH' || planCode === 'pro') displayLimit = '15';
                              else if (planCode === 'BUSINESS' || planCode === 'business') displayLimit = '∞';
                              else if (planCode === 'ENTERPRISE' || planCode === 'enterprise') displayLimit = '∞';
                              else displayLimit = '1'; // Default for TRIAL
                            } else {
                              displayLimit = limit.toString();
                            }

                            return `${teamMembers.filter(member => member.access_status !== 'suspended_plan_limit').length} of ${displayLimit} members used`;
                          })()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          {(() => {
                            // Get team member limit from subscription data
                            const limit = globalDetailedUsage?.team_members_limit || globalUsage?.team_members_limit;
                            let actualLimit;

                            if (limit === -1) {
                              return 'Unlimited';
                            } else if (limit === undefined || limit === null) {
                              // Fallback to plan-based limits if subscription data not available
                              const planCode = globalDetailedUsage?.plan_code || globalUsage?.current_plan;
                              if (planCode === 'STARTER' || planCode === 'starter') actualLimit = 10;
                              else if (planCode === 'GROWTH' || planCode === 'pro') actualLimit = 15;
                              else if (planCode === 'BUSINESS' || planCode === 'business') return 'Unlimited';
                              else if (planCode === 'ENTERPRISE' || planCode === 'enterprise') return 'Unlimited';
                              else actualLimit = 1; // Default for TRIAL
                            } else {
                              actualLimit = limit;
                            }

                            const remaining = actualLimit - teamMembers.length;
                            return remaining > 0 ? `${remaining} remaining` : 'Limit reached';
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Organization Settings - Now in Settings → Organization Settings */}
                {/* Project Management - Now in separate "Projects" menu item in sidebar */}
              </div>
  );
}
